import React, { useState } from "react";
import { Collapse, Typography, Divider, Tabs } from "antd";
import InformationIcon from "../Shared/icons/InformationIcon";
import { Configuration } from "src/types/Redux";
import { ReduxApplication } from "src/types/Application";
import { getRiskLevel, formatDate } from "src/utils/Utils";
import "./RiskAssessment.scss";

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface RiskAssessmentProps {
  application: ReduxApplication;
  configurationData: Configuration;
}

const RiskAssessment: React.FC<RiskAssessmentProps> = ({
  configurationData,
  application,
}) => {
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);

  const applicants = application.applicants || [];
  const enrichedApplicants = applicants
    .map((app) => {
      const riskInfo = getRiskLevel(configurationData ?? {}, app.riskScore);

      return {
        ...app,
        riskScore: riskInfo.percentage,
        riskLevel: riskInfo.risk,
        riskColor: riskInfo.color,
      };
    })
    .sort((a, b) => a.id - b.id);

  const realActiveKey =
    activeTabKey ||
    (enrichedApplicants?.length > 0 ? String(enrichedApplicants[0].id) : "");

  return (
    <Collapse defaultActiveKey={["riskAssessment"]} bordered={false}>
      <Panel
        header="RISK ASSESSMENT"
        key="riskAssessment"
        className="risk-tabs"
      >
        <Tabs
          className="custom-tabs"
          type="line"
          tabPosition="top"
          activeKey={realActiveKey}
          onChange={(key) => setActiveTabKey(key)}
          items={enrichedApplicants.map((applicant) => ({
            key: String(applicant.id),
            label: (
              <div
                className={`tab-label ${
                  realActiveKey === String(applicant.id) ? "active" : ""
                }`}
              >
                {applicant.firstName} {applicant.lastName}
              </div>
            ),
            children: (
              <div className="risk-container">
                <div className="risk-left">
                  <div className="risk-info">
                    {applicant.riskLevel ? (
                      <>
                        <InformationIcon
                          className="information-icon"
                          color={String(applicant.riskColor)}
                        />
                        <Text strong className="risk-text">
                          {applicant.riskLevel}
                        </Text>
                      </>
                    ) : (
                      <div className="risk-placeholder" />
                    )}
                  </div>
                  <Divider className="divider-compact" />

                  <Title level={5}>Applicant Details</Title>

                  <div className="details-wrapper">
                    {[
                      ["First Name", applicant.firstName],
                      ["Last Name", applicant.lastName],
                      ["Date of Birth", formatDate(applicant.dob)],
                      [
                        "Address",
                        `${applicant.address1 || "--"} ${
                          applicant.city || "--"
                        } ${applicant.state || "--"} ${applicant.zip || "--"}`,
                      ],
                      ["Phone Number", applicant.phoneNumber],
                      ["Email", applicant.emailAddress],
                      [
                        "Employer Name",
                        applicant.applicantEmployer?.[0]?.employer,
                      ],
                      [
                        "Employer Address",
                        `${
                          applicant.applicantEmployer?.[0]?.address1 || "--"
                        } ${applicant.applicantEmployer?.[0]?.city || "--"} ${
                          applicant.applicantEmployer?.[0]?.state || "--"
                        } ${applicant.applicantEmployer?.[0]?.zip || "--"}`,
                      ],
                      [
                        "Employer Phone",
                        applicant.applicantEmployer?.[0]?.employerPhone,
                      ],
                    ].map(([label, value]) => (
                      <React.Fragment key={label}>
                        <div className="detail-row">
                          <Text className="detail-label">{label}</Text>
                          <Text className="detail-value">{value || "--"}</Text>
                        </div>
                        <Divider className="divider-compact" />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            ),
          }))}
        />
      </Panel>
    </Collapse>
  );
};

export default RiskAssessment;
